import axios from 'axios';
import type {
  RetentionPolicyListItem,
  RetentionPolicyResponse,
  CreateRetentionPolicyRequest,
  UpdateRetentionPolicyRequest,
  EnabledToggleRequest,
  DryRunResponse,
  PurgeRequest,
  PurgeResponse,
  RetentionRunItem,
  PageResponse,
} from '../types/retentionAdmin';
import { getStoredToken } from '../../login/api/loginApi';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

// Bearer 토큰 부착
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// ApiResponse<T> 언래핑
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? '알 수 없는 오류',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      '서버에 연결할 수 없습니다';
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, traceId });
  }
);

export const retentionAdminApi = {
  /** GET /retention-admin/policies — 목록 검색 (RETENTION_READ) */
  listPolicies: (params: {
    table?: string;
    mode?: string;
    enabled?: string;
    keyword?: string;
    page?: number;
    size?: number;
    sort?: string;
  }): Promise<PageResponse<RetentionPolicyListItem>> => {
    const sp = new URLSearchParams();
    if (params.table) sp.set('table', params.table);
    if (params.mode) sp.set('mode', params.mode);
    if (params.enabled !== undefined && params.enabled !== '') sp.set('enabled', params.enabled);
    if (params.keyword) sp.set('keyword', params.keyword);
    if (params.page !== undefined) sp.set('page', String(params.page));
    if (params.size !== undefined) sp.set('size', String(params.size));
    if (params.sort) sp.set('sort', params.sort);
    return apiClient
      .get<unknown, { data: PageResponse<RetentionPolicyListItem> }>(
        `/retention-admin/policies?${sp.toString()}`
      )
      .then((res) => res.data);
  },

  /** GET /retention-admin/policies/{id} — 상세 (RETENTION_READ) */
  getPolicy: (id: number): Promise<RetentionPolicyResponse> =>
    apiClient
      .get<unknown, { data: RetentionPolicyResponse }>(`/retention-admin/policies/${id}`)
      .then((res) => res.data),

  /** POST /retention-admin/policies — 생성 201 (RETENTION_MANAGE) */
  createPolicy: (body: CreateRetentionPolicyRequest): Promise<RetentionPolicyResponse> =>
    apiClient
      .post<unknown, { data: RetentionPolicyResponse }>('/retention-admin/policies', body)
      .then((res) => res.data),

  /** PUT /retention-admin/policies/{id} — 수정 (RETENTION_MANAGE) */
  updatePolicy: (id: number, body: UpdateRetentionPolicyRequest): Promise<RetentionPolicyResponse> =>
    apiClient
      .put<unknown, { data: RetentionPolicyResponse }>(`/retention-admin/policies/${id}`, body)
      .then((res) => res.data),

  /** PATCH /retention-admin/policies/{id}/enabled — 활성/비활성 토글 (RETENTION_MANAGE) */
  toggleEnabled: (id: number, body: EnabledToggleRequest): Promise<RetentionPolicyResponse> =>
    apiClient
      .patch<unknown, { data: RetentionPolicyResponse }>(
        `/retention-admin/policies/${id}/enabled`,
        body
      )
      .then((res) => res.data),

  /** DELETE /retention-admin/policies/{id} — 소프트 삭제 204 (RETENTION_MANAGE) */
  deletePolicy: (id: number): Promise<void> =>
    apiClient
      .delete<unknown, { data: null }>(`/retention-admin/policies/${id}`)
      .then(() => undefined),

  /** POST /retention-admin/policies/{id}/dry-run — 미리보기 비파괴 (RETENTION_READ) */
  dryRun: (id: number): Promise<DryRunResponse> =>
    apiClient
      .post<unknown, { data: DryRunResponse }>(`/retention-admin/policies/${id}/dry-run`)
      .then((res) => res.data),

  /** POST /retention-admin/policies/{id}/purge — 물리 삭제 파괴적 (RETENTION_PURGE) */
  purge: (id: number, body: PurgeRequest): Promise<PurgeResponse> =>
    apiClient
      .post<unknown, { data: PurgeResponse }>(`/retention-admin/policies/${id}/purge`, body)
      .then((res) => res.data),

  /** GET /retention-admin/runs — 실행 이력 (RETENTION_READ) */
  listRuns: (params: {
    policyName?: string;
    status?: string;
    page?: number;
    size?: number;
    sort?: string;
  }): Promise<PageResponse<RetentionRunItem>> => {
    const sp = new URLSearchParams();
    if (params.policyName) sp.set('policyName', params.policyName);
    if (params.status) sp.set('status', params.status);
    if (params.page !== undefined) sp.set('page', String(params.page));
    if (params.size !== undefined) sp.set('size', String(params.size));
    if (params.sort) sp.set('sort', params.sort);
    return apiClient
      .get<unknown, { data: PageResponse<RetentionRunItem> }>(
        `/retention-admin/runs?${sp.toString()}`
      )
      .then((res) => res.data);
  },
};
